import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import paymentService from '../services/paymentService';
import '../styles/PaymentInstructionsPage.css';

const PaymentInstructionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;
  
  const { method, amount, plan, planName, durationDays } = location.state || {};
  
  const [paymentId, setPaymentId] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Initiate payment when page loads (record timestamp)
  useEffect(() => {
    const initiatePayment = async () => {
      if (!user || !method || paymentId) return;

      try {
        const result = await paymentService.initiatePayment({
          userId: user.uid,
          subscriptionPlan: plan || '24h',
          subscriptionDuration: durationDays || 1,
          amount: amount || 299,
          method: method || 'gcash'
        });
        setPaymentId(result.paymentId);
        console.log('Payment initiated:', result.paymentId);
      } catch (err) {
        console.error('Failed to initiate payment:', err);
        setError('Failed to initiate payment. Please try again.');
      }
    };

    initiatePayment();
  }, [user, method, plan, durationDays, amount, paymentId]);

  // Determine QR code image based on amount
  const getQRImage = (paymentAmount) => {
    const amt = Number(paymentAmount);
    if (amt === 149) return '/images/payment/qrPayment_6hr.jpg';
    if (amt === 229) return '/images/payment/qrPayment_12hr.jpg';
    if (amt === 299) return '/images/payment/qrPayment_24hr.jpg';
    if (amt === 999) return '/images/payment/qrPayment_monthly.jpg';
    return '/images/payment/qrPayment_24hr.jpg'; // fallback to 24hr
  };

  // Payment details based on method
  const paymentDetails = {
    gcash: {
      name: 'GCash',
      qrImage: getQRImage(amount),
      instructions: [
        'Download the QR code below',
        'Open your GCash app',
        'Tap "Send Money" then "QR"',
        'Upload the QR code from your gallery',
        'Verify the amount is ₱' + (amount || 299),
        'Complete the transaction',
        'Take a screenshot of the receipt',
        'Upload the screenshot below'
      ]
    },
    maya: {
      name: 'Maya',
      qrImage: getQRImage(amount),
      instructions: [
        'Download the QR code below',
        'Open your Maya app',
        'Tap "Send Money" then "QR"',
        'Upload the QR code from your gallery',
        'Verify the amount is ₱' + (amount || 299),
        'Complete the transaction',
        'Take a screenshot of the receipt',
        'Upload the screenshot below'
      ]
    }
  };

  const currentPayment = paymentDetails[method] || paymentDetails.gcash;

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.href = currentPayment.qrImage;
    link.download = 'pocket-videoke-payment-qr.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB');
        return;
      }

      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!screenshot) {
      setError('Please upload a payment screenshot');
      return;
    }

    if (!paymentId) {
      setError('Payment not initiated. Please refresh the page.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('paymentScreenshot', screenshot);
      formData.append('paymentId', paymentId); // Pass paymentId instead of all other fields

      const result = await paymentService.uploadPaymentScreenshot(formData);
      
      // Navigate to confirmation page
      navigate('/payment/confirmation', { 
        state: { 
          paymentId: result.paymentId,
          status: 'pending'
        }
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload screenshot. Please try again.';
      const errorDetails = err.response?.data?.details || '';
      setError(errorMessage + (errorDetails ? ': ' + errorDetails : ''));
      console.error('Upload error:', err);
      console.error('Error response:', err.response?.data);
    } finally {
      setUploading(false);
    }
  };

  if (!user || !method) {
    navigate('/payment');
    return null;
  }

  return (
    <div className="payment-instructions-page">
      <div className="instructions-container">
        <h1>Payment Instructions</h1>
        
        <div className="payment-method-header">
          <img src={method === 'gcash' ? '/images/payment/gcash_logo.png' : '/images/payment/maya_logo.png'} alt={currentPayment.name} />
          <h2>Pay via {currentPayment.name}</h2>
        </div>

        <div className="payment-amount">
          <h3>Amount to Pay</h3>
          <p className="amount">₱{amount || 299}</p>
          <p className="plan-name">{planName || 'Day Pass'}</p>
        </div>

        <div className="qr-code-section">
          <h3>Payment QR Code</h3>
          <p className="qr-note">Download this QR code and scan it in your {currentPayment.name} app</p>
          
          <div className="qr-container">
            <img src={currentPayment.qrImage} alt="Payment QR Code" className="qr-image" />
          </div>
          
          <button className="download-qr-button" onClick={handleDownloadQR}>
            <i className="fas fa-download"></i> Download QR Code
          </button>
        </div>

        <div className="instructions-list">
          <h3>Steps to Complete Payment</h3>
          <ol>
            {currentPayment.instructions.map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ol>
        </div>

        <div className="screenshot-upload">
          <h3>Upload Payment Receipt</h3>
          <p className="upload-note">Upload a clear screenshot of your payment receipt</p>
          
          <div className="upload-area">
            <input
              type="file"
              id="screenshot-input"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="screenshot-input" className="upload-button">
              {previewUrl ? 'Change Screenshot' : 'Choose Screenshot'}
            </label>
            
            {previewUrl && (
              <div className="preview-container">
                <img src={previewUrl} alt="Payment receipt preview" />
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={!screenshot || uploading}
          >
            {uploading ? 'Uploading...' : 'Submit Payment'}
          </button>
        </div>

        <button className="back-button" onClick={() => navigate('/payment')}>
          ← Back to Payment Methods
        </button>
      </div>
    </div>
  );
};

export default PaymentInstructionsPage;
